/**
 * @file SSOInitiationTypeDropdown.js
 * @description A dropdown component for selecting SSO initiation types.
 */

import React, { useState, useEffect } from "react";
import { fetchSSOInitiationTypes } from "../../../services/ssoService";
import { Dropdown } from "../../helpers/Dropdown";
import LoadingWrapper from "../../ui/loaders/LoadingWrapper";
import Toast from "../../ui/toasts/Toast";
import { getText } from '../../../i18n';

/** 
 * Represents a dropdown component for selecting SSO initiation types.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines whether the label is displayed inline with the dropdown.
 * @param {string} [props.selectedType] - The ID of the selected SSO initiation type.
 * @param {function} props.onTypeChange - The callback function to be called when the selected type changes.
 * @param {string} [props.label="SSO Initiation Type"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines whether the dropdown is required.
 * @param {boolean} [props.allowNullSelection=false] - Determines whether a null selection is allowed.
 * @returns {JSX.Element} The rendered SSOInitiationTypeDropdown component.
 */
const SSOInitiationTypeDropdown = ({
  className,
  inlineLabel = true,
  selectedType,
  onTypeChange,
  label = "SSO Initiation Type",
  placeholder = "Select SSO Initiation Type",
  isRequired = false,
  allowNullSelection = false,
}) => {
  const [initiationTypes, setInitiationTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getInitiationTypes = async () => {
      setIsLoading(true);
      try {
        const data = await fetchSSOInitiationTypes();
        setInitiationTypes(data);
      } catch (error) {
        console.error("Could not fetch SSO initiation types:", error);
        Toast.error(getText('msg_1cea69aa67be'));
      } finally {
        setIsLoading(false);
      }
    };

    getInitiationTypes();
  }, []);

  const getDefaultLabel = () => {
    const matchingType = initiationTypes.find(
      (type) => type.id === selectedType
    );
    return matchingType ? matchingType.name : placeholder;
  };

  const typeOptions = allowNullSelection
    ? [
        { value: null, label: placeholder },
        ...initiationTypes.map((type) => ({
          value: type.id,
          label: type.name,
        })),
      ]
    : initiationTypes.map((type) => ({
        value: type.id,
        label: type.name,
      }));

  return (
    <div
      className={`form-group mb-3 ${
        inlineLabel ? "inline-input" : ""
      } ${className}`}
    >
      <label htmlFor="sso-initiation-type-dropdown">
        {label}
        {isRequired && <span className="text-danger">*</span>}
      </label>
      <LoadingWrapper isLoading={isLoading}>
        <Dropdown
          id="sso-initiation-type-dropdown"
          defaultValue={getDefaultLabel()}
          options={typeOptions}
          value={selectedType}
          onChange={onTypeChange}
          disabled={initiationTypes.length === 0 || isLoading}
        />
      </LoadingWrapper>
    </div>
  );
};

export default SSOInitiationTypeDropdown;
